import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Paintbrush, ShowerHead, Sofa, Wrench } from "lucide-react";
import SiteLayout from "@/components/layout/SiteLayout";
import PageHero from "@/components/sections/PageHero";
import ContentSection from "@/components/sections/ContentSection";
import SectionHeader from "@/components/sections/SectionHeader";
import SplitSection from "@/components/sections/SplitSection";
import { Button } from "@/components/ui/button";
import heroImage from "@/assets/carpentry-hero.jpg";
import supportImage from "@/assets/bg-interior-2.jpg";
import sellerImage from "@/assets/service-sell.jpg";
import notSellingImage from "@/assets/about-lifestyle.jpg";
import cleaningImage from "@/assets/cleaning-hero.jpg";
import paintingImage from "@/assets/painting-hero.jpg";
import interiorDesignImage from "@/assets/interior-design-hero.jpg";
import beforeAfterImage from "@/assets/bg-interior-1.jpg";
import propertyPrepImage from "@/assets/property-prep-hero.jpg";

const negotiationQuestions = [
  "What else has not been maintained?",
  "What will come up during inspection?",
  "How much will this cost me after I buy?",
];

const negotiationOutcomes = [
  "Lower offers",
  "Repair credits",
  "Delays during closing",
];

const highImpactRepairs = [
  {
    label: "Repair Focus",
    title: "Door alignment and repairs",
    image: heroImage,
    imageAlt: "Door alignment and carpentry repair detail",
  },
  {
    label: "Repair Focus",
    title: "Trim, molding, and baseboard fixes",
    image: supportImage,
    imageAlt: "Trim, molding, and baseboard repair detail",
  },
  {
    label: "Repair Focus",
    title: "Minor drywall and surface repairs",
    image: propertyPrepImage,
    imageAlt: "Minor drywall and surface repair work",
  },
  {
    label: "Repair Focus",
    title: "Cabinet adjustments and hardware fixes",
    image: sellerImage,
    imageAlt: "Cabinet adjustment and hardware repair detail",
  },
  {
    label: "Repair Focus",
    title: "General wear and tear corrections",
    image: notSellingImage,
    imageAlt: "General wear and tear carpentry correction",
  },
];

const beforeItems = [
  "Visible damage or unfinished details",
  "Small issues that raise questions",
  "A home that feels less cared for",
];

const afterItems = [
  "Clean, finished, and cohesive appearance",
  "Stronger buyer confidence",
  "A smoother inspection process",
  "Fewer objections during negotiations",
];

const sellerBenefits = [
  "Prevent issues during inspection",
  "Reduce buyer negotiation power",
  "Support stronger offers",
  "Help your home feel move-in ready",
];

const relatedServices = [
  {
    title: "Cleaning services to create a polished finish",
    description:
      "A professionally cleaned home helps every repair and improvement read more clearly.",
    href: "/cleaning",
    ctaLabel: "Explore Cleaning",
    image: cleaningImage,
    imageAlt: "Cleaning services as part of home preparation",
    icon: ShowerHead,
  },
  {
    title: "Painting services to refresh and modernize",
    description:
      "Painting helps repaired spaces feel intentional, updated, and ready for the market.",
    href: "/painting",
    ctaLabel: "Explore Painting",
    image: paintingImage,
    imageAlt: "Painting services for refreshed home presentation",
    icon: Paintbrush,
  },
  {
    title: "Interior design guidance to improve presentation",
    description:
      "Design guidance helps the finished home feel cohesive, inviting, and market-ready.",
    href: "/interior-design",
    ctaLabel: "Explore Interior Design",
    image: interiorDesignImage,
    imageAlt: "Interior design guidance for better home presentation",
    icon: Sofa,
  },
];

const expectations = [
  "Clear guidance on what needs to be fixed and what does not",
  "Focus on high-impact repairs only",
  "Clean, professional work",
  "A home that feels complete and well-maintained",
];

const CarpentryPage = () => {
  useEffect(() => {
    document.title = "Carpentry and Home Repair Services in Massachusetts | Hadassah Homes";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "Fix the issues buyers notice. Hadassah Homes provides professional carpentry and repair services to protect your home's value and strengthen your sale."
      );
    }
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = "https://hadassahhomesinc.com/carpentry";
  }, []);

  return (
    <SiteLayout>
      <PageHero
        eyebrow="Carpentry Services"
        title="Carpentry and Home Repair Services in Massachusetts"
        subtitle="Fix the Issues Buyers Notice Before They Use Them Against You"
        backgroundImage={heroImage}
      >
        <div className="mx-auto max-w-xl space-y-5 md:space-y-6">
          <p className="text-white text-lg md:text-xl leading-[1.75]">
            Small visible issues create buyer doubt, and buyer doubt creates negotiation leverage.
          </p>
          <Button variant="gold" size="lg" asChild>
            <Link to="/consultation">
              Book a Consultation
            </Link>
          </Button>
        </div>
      </PageHero>

      <ContentSection>
        <SplitSection
          title="Buyers Notice More Than You Think"
          description="Most buyers won't say it out loud, but they notice everything."
          image={supportImage}
          imageAlt="Interior detail showing trim and finish quality"
        >
          <div className="space-y-4 text-muted-foreground text-base md:text-lg leading-[1.85]">
            <p>Loose trim. Damaged doors. Worn finishes. Small repairs left undone.</p>
            <p>These details create doubt. And doubt leads to lower offers, tougher negotiations, and problems during inspection.</p>
            <p>At Hadassah Homes, we help you fix the right things before your home hits the market so you can present a property that feels solid, well-maintained, and move-in ready.</p>
          </div>
          <div className="pt-3">
            <Link
              to="/consultation"
              className="inline-flex items-center gap-2 font-body text-sm uppercase tracking-[0.12em] text-accent hover:text-accent/80 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              Schedule your repair assessment today
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </SplitSection>
      </ContentSection>

      <ContentSection variant="dark">
        <div className="grid grid-cols-1 lg:grid-cols-[0.42fr_0.58fr] gap-12 md:gap-16 lg:gap-20 items-start">
          <div className="space-y-6 md:space-y-7">
            <SectionHeader
              title="Small Issues Create Big Negotiation Leverage"
              align="left"
            />
            <p className="text-secondary-foreground/82 text-base md:text-lg leading-[1.85]">
              Buyers are not just looking at your home. They are looking for reasons to negotiate.
            </p>
            <p className="text-secondary-foreground/82 text-base md:text-lg leading-[1.85]">
              Fixing these issues early removes that leverage.
            </p>
          </div>

          <div className="space-y-8 md:space-y-10 border-l border-accent/22 pl-6 md:pl-8">
            <div className="space-y-5 md:space-y-6">
              {negotiationQuestions.map((item, index) => (
                <div
                  key={item}
                  className={index < negotiationQuestions.length - 1 ? "border-b border-secondary-foreground/10 pb-5 md:pb-6" : ""}
                >
                  <p className="font-headline text-[1.45rem] md:text-[1.9rem] text-secondary-foreground leading-[1.25]">
                    {item}
                  </p>
                </div>
              ))}
            </div>

            <div className="pt-2 border-t border-accent/28">
              <p className="font-body text-[10px] uppercase tracking-[0.18em] text-accent mb-4">
                What that becomes
              </p>
              <div className="space-y-4">
                {negotiationOutcomes.map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <span className="mt-2 h-1.5 w-1.5 bg-accent shrink-0" />
                    <p className="text-secondary-foreground text-base md:text-lg leading-[1.75]">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </ContentSection>

      <ContentSection>
        <SectionHeader
          title="We Focus on What Actually Impacts the Sale"
          description="This is not about over-renovating your home. It is about fixing the specific issues that influence buyer confidence, inspection results, and perceived value."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-8">
          {highImpactRepairs.map((item) => (
            <article
              key={item.title}
              className="group relative min-h-[360px] overflow-hidden border border-border/60"
            >
              <img
                src={item.image}
                alt={item.imageAlt}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/45 to-transparent" />
              <div className="relative z-10 flex min-h-[360px] items-end p-6 md:p-8">
                <div className="max-w-[26rem]">
                  <p className="mb-3 font-body text-[10px] uppercase tracking-[0.2em] text-white/70">
                    {item.label}
                  </p>
                  <h3 className="font-headline text-[1.7rem] md:text-[1.95rem] leading-[1.12] text-white">
                    {item.title}
                  </h3>
                </div>
              </div>
            </article>
          ))}
        </div>
        <p className="max-w-3xl mx-auto pt-10 md:pt-12 text-center text-muted-foreground text-base md:text-lg leading-[1.8]">
          These are the details that separate a home that feels maintained from one that feels neglected.
        </p>
      </ContentSection>

      <ContentSection variant="dark">
        <SectionHeader
          title="Before and After Repairs"
          description="The goal is not perfection. The goal is confidence."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-secondary-foreground/12">
          <article className="border-b border-secondary-foreground/12 md:border-b-0 md:border-r md:border-r-accent/28 bg-secondary/85">
            <div className="relative aspect-[16/11] overflow-hidden">
              <img
                src={beforeAfterImage}
                alt="Home detail before carpentry repairs"
                className="absolute inset-0 h-full w-full object-cover brightness-[0.62] saturate-[0.72]"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/40" />
              <div className="absolute left-5 top-5 z-10 border border-white/18 bg-black/58 px-3 py-1.5">
                <span className="font-body text-[10px] uppercase tracking-[0.18em] text-white">
                  Before
                </span>
              </div>
            </div>
            <div className="px-6 py-6 md:px-8 md:py-8">
              <div className="space-y-4 max-w-md">
                {beforeItems.map((item) => (
                  <p key={item} className="text-secondary-foreground/82 text-base md:text-lg leading-[1.8]">
                    {item}
                  </p>
                ))}
              </div>
            </div>
          </article>

          <article className="bg-secondary/85">
            <div className="relative aspect-[16/11] overflow-hidden">
              <img
                src={beforeAfterImage}
                alt="Home detail after carpentry repairs"
                className="absolute inset-0 h-full w-full object-cover brightness-[1.04] saturate-[1.02] contrast-[1.03]"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/26 via-black/10 to-transparent" />
              <div className="absolute left-5 top-5 z-10 border border-accent/35 bg-black/34 px-3 py-1.5">
                <span className="font-body text-[10px] uppercase tracking-[0.18em] text-white">
                  After
                </span>
              </div>
            </div>
            <div className="px-6 py-6 md:px-8 md:py-8">
              <div className="space-y-4 max-w-md">
                {afterItems.map((item) => (
                  <p key={item} className="text-secondary-foreground/82 text-base md:text-lg leading-[1.8]">
                    {item}
                  </p>
                ))}
              </div>
            </div>
          </article>
        </div>
      </ContentSection>

      <ContentSection variant="dark">
        <SplitSection
          title="For Sellers: Protect Your Price and Your Deal"
          description="Addressing repairs before listing can:"
          image={sellerImage}
          imageAlt="Seller preparation and carpentry improvements before listing"
          reverse
        >
          <div className="space-y-4 text-secondary-foreground/82 text-base md:text-lg leading-[1.85]">
            {sellerBenefits.map((item) => (
              <div key={item} className="flex items-start gap-3">
                <Check className="mt-1 h-4 w-4 text-accent shrink-0" />
                <p>{item}</p>
              </div>
            ))}
            <p className="pt-2">
              This is one of the most practical ways to protect your bottom line.
            </p>
          </div>
          <div className="pt-3">
            <Link
              to="/sell-your-home"
              className="inline-flex items-center gap-2 font-body text-sm uppercase tracking-[0.12em] text-accent hover:text-accent/80 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-secondary"
            >
              Explore Sell Your Home
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </SplitSection>
      </ContentSection>

      <ContentSection>
        <SplitSection
          title="Not Selling? Still Worth Fixing"
          description="These repairs are not just for sellers."
          image={notSellingImage}
          imageAlt="Well-maintained home interior for everyday living"
          reverse
        >
          <div className="space-y-4 text-muted-foreground text-base md:text-lg leading-[1.85]">
            <p>If you plan to stay in your home, addressing these issues improves daily comfort, prevents bigger problems later, and helps maintain your property's value.</p>
            <p>A well-maintained home is easier to live in and easier to sell when the time comes.</p>
          </div>
        </SplitSection>
      </ContentSection>

      <ContentSection>
        <SectionHeader
          title="Part of a Complete Preparation Strategy"
          description="Carpentry works best when combined with other key improvements."
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {relatedServices.map((item) => (
            <article
              key={item.title}
              className="border border-border/65 bg-card overflow-hidden"
            >
              <div className="aspect-[16/11] overflow-hidden">
                <img
                  src={item.image}
                  alt={item.imageAlt}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="space-y-4 px-6 py-6 md:px-7 md:py-7">
                <div className="flex items-center gap-3 text-accent">
                  <item.icon className="h-4 w-4" />
                  <span className="font-body text-[10px] uppercase tracking-[0.16em]">
                    Related Service
                  </span>
                </div>
                <h3 className="font-headline text-2xl text-foreground leading-[1.2]">
                  {item.title}
                </h3>
                <p className="text-muted-foreground text-base leading-[1.75]">
                  {item.description}
                </p>
                <Link
                  to={item.href}
                  className="inline-flex items-center gap-2 font-body text-sm uppercase tracking-[0.12em] text-accent hover:text-accent/80 transition-colors"
                >
                  {item.ctaLabel}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </article>
          ))}
        </div>
        <div className="max-w-3xl mx-auto pt-14 md:pt-16">
          <div className="w-16 h-px bg-accent/45 mx-auto mb-6" />
          <p className="text-center text-muted-foreground text-lg md:text-[1.18rem] tracking-[0.01em] leading-[1.85]">
            Together, these create a home that looks better, feels better, and performs better in the market.
          </p>
        </div>
      </ContentSection>

      <ContentSection variant="dark">
        <div className="grid grid-cols-1 lg:grid-cols-[0.42fr_0.58fr] gap-12 md:gap-16 lg:gap-20 items-start">
          <div className="space-y-6 md:space-y-7">
            <SectionHeader
              title="What You Can Expect"
              align="left"
            />
            <p className="text-secondary-foreground/82 text-base md:text-lg leading-[1.85]">
              We do not focus on unnecessary work. We focus on what actually makes a difference.
            </p>
          </div>

          <div className="space-y-7 md:space-y-8 border-l border-accent/22 pl-6 md:pl-8">
            {expectations.map((item, index) => (
              <div
                key={item}
                className={index < expectations.length - 1 ? "border-b border-secondary-foreground/10 pb-7 md:pb-8" : ""}
              >
                <p className="font-headline text-[1.35rem] md:text-[1.85rem] text-secondary-foreground leading-[1.28]">
                  {item}
                </p>
              </div>
            ))}
          </div>
        </div>
      </ContentSection>

      <section className="relative overflow-hidden py-28 md:py-36">
        <img
          src={heroImage}
          alt="Home prepared with carpentry and repair work completed"
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/94 via-black/80 to-black/58" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/40 to-transparent" />

        <div className="relative z-10 mx-auto max-w-content px-5 md:px-12">
          <div className="mx-auto max-w-[42rem] text-center">
            <h2 className="font-headline text-3xl sm:text-4xl md:text-[3.45rem] leading-[1.08] text-white mb-5">
              Let’s Get Your Home Ready the Right Way
            </h2>
            <p className="text-white/90 text-base md:text-lg leading-[1.8] max-w-[36rem] mx-auto mb-12">
              The best time to fix these issues is before buyers ever walk through the door. We will help you identify what matters, take care of it efficiently, and position your home for a stronger outcome.
            </p>
            <Button variant="gold" size="lg" asChild>
              <Link to="/consultation">
                Book a Consultation
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
};

export default CarpentryPage;
